import axios from 'axios';
import { toast } from 'react-toastify';
import store from '../../../app/store/store';
import { incrementLoading, decrementLoading } from '../../../app/store/slices/loadingSlice';

//const API_URL = 'http://localhost:5199/api/';
const API_URL = 'https://dev-billeur-api-webapp-f3cnbwfybmhdbjap.westeurope-01.azurewebsites.net/api/';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // ✅ Always send HTTP-only cookies
  timeout: 300000,
});

// Function to handle logout
const handleLogout = () => {
  
  localStorage.removeItem('user');
  toast.warning('Session expired. Please login again.');
  
  setTimeout(() => {
    window.location.href = '/login';
  }, 1000);
};

axiosInstance.interceptors.request.use(
  (config) => {
    // ✅ Increment global loading counter
    store.dispatch(incrementLoading());
    
    // ✅ Remove Content-Type header for FormData to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // ✅ No manual token handling - HTTP-only cookies are sent automatically
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    // ✅ Decrement loading counter on error
    store.dispatch(decrementLoading());
    toast.error('Request failed to send');
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // ✅ Decrement global loading counter on success
    store.dispatch(decrementLoading());
    
    // ✅ Check for backend error format with errorCode > 0
    const data = response.data;
    if (data && typeof data.errorCode === 'number' && data.errorCode > 0) {
      // Backend returned an error
      const errorMessage = data.message || 'An error occurred';
      toast.error(errorMessage);
      
      // Reject the promise so catch blocks handle it
      return Promise.reject({
        isBackendError: true,
        errorCode: data.errorCode,
        message: errorMessage,
        status: data.status,
        statusCode: data.statusCode,
        response: { data }
      });
    }
    
    return response;
  },
  (error) => {
    // ✅ Decrement global loading counter on error
    store.dispatch(decrementLoading());
    console.error('❌ Protected API Error:', error);
    
    if (error.response) {
      const { status, data, config } = error.response;
      
      // ✅ Check for backend error format with errorCode > 0
      if (data && typeof data.errorCode === 'number' && data.errorCode > 0) {
        const errorMessage = data.message || 'An error occurred';
        toast.error(errorMessage);
        return Promise.reject({
          isBackendError: true,
          errorCode: data.errorCode,
          message: errorMessage,
          status: data.status,
          statusCode: data.statusCode,
          response: error.response
        });
      }
      
      // Check if this is a logout request
      const isLogoutRequest = config.url && config.url.includes('Account/logout');
      
      switch (status) {
        case 401:
          handleLogout();
          break;
          
        case 403:
          if (!isLogoutRequest) {
            toast.error(data.message || 'Access denied to this resource');
          }
          break;
          
        case 404:
          // Don't show error toast for logout requests
          if (!isLogoutRequest) {
            toast.error(data.message || 'Resource not found');
          }
          break;
          
        case 500:
          if (!isLogoutRequest) {
            toast.error('Server error occurred. Please try again later.');
          }
          break;
          
        default:
          if (!isLogoutRequest) {
            toast.error(data.message || `Error: ${status}`);
          }
      }
    } else if (error.request) {
      console.error('🌐 Network Error:', error.request);
      toast.error('Network error - Please check your connection');
    } else {
      console.error('❌ Request Setup Error:', error.message);
      toast.error(error.message || 'An unexpected error occurred');
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;